import { action, computed, observable } from "mobx";
import { parse } from "qs";

import { BaseStore } from "./BaseStore";
import { CacheStore } from "./CacheStore/CacheStore";
import { LocalPubSubStore } from "./LocalPubSubStore";
import { MessagingStore } from "./MessagingStore";
import { RoutingStore } from "./RoutingStore/RoutingStore";
import { SnackbarStore } from "./SnackbarStore";
import { TodoStore } from "./TodoStore/TodoStore";
import { UIStore } from "./UIStore";
import { UploadStore } from "./UploadStore/UploadStore";
import { UserStore } from "./UserStore/UserStore";
import { UserService } from "./UserService";
import { WorkflowTasksStore } from "./WorkflowTasksStore";
import { NavigationManager } from "../navigation";
import { ConsultantFavouritesStore } from "../consultants/consultant-favourites/consultant-favourites-store";

import { GeneralSearchStore } from "../search/GeneralSearchStore";
import { EmailEditorDialogStore } from "../containers/EmailEditor/EmailEditorDialogStore";
import { registerDirectUrls } from "../direct_urls";
import { environment } from "../../environments/environment";
import { semverToInt } from "@bonliva-crm/common";

declare global {
  interface Window {
    store: Store;
  }
}

export class Store extends BaseStore {
  static Instance: Store;

  static PARSE_URL() {
    const { location } = window;
    const params = parse(location.search, { ignoreQueryPrefix: true });

    sessionStorage.removeItem("singInAsUser");

    if (params.singInAsUser) {
      sessionStorage.setItem("singInAsUser", params.singInAsUser as string);
    }

    window.history.pushState(
      {},
      "",
      `${location.origin}${location.pathname}${location.hash}${location.search}`
    );
  }

  cache = new CacheStore(); // Cached data to be used across the app
  generalSearchStore: GeneralSearchStore; // for search pages
  localPubSub = new LocalPubSubStore();
  navigation = new NavigationManager();
  messaging = new MessagingStore(this.navigation);
  routing = new RoutingStore(); // react-router plus custom methods
  snackbars = new SnackbarStore(); // snackbars
  ui = new UIStore(this); // some ui state
  upload = new UploadStore(this); // some uploading functions
  consultantFavourites = new ConsultantFavouritesStore();
  user = new UserStore(this.snackbars, this.cache, this.ui, this.consultantFavourites); // user profile and such
  todoStore = new TodoStore(this.snackbars, this.user, this.ui); // user's todos
  emailEditor = new EmailEditorDialogStore();
  workflowTasks = new WorkflowTasksStore();

  userService = new UserService(this.cache, this.user);

  @observable minVersion: number = null;
  currentVersion = semverToInt(environment.version);
  @observable snoozeUpdateUntil = Date.parse("1980-01-01").valueOf();

  @computed get profile() {
    return this.user.myProfile;
  }

  @computed get locationState() {
    return this.routing.location.state || {};
  }

  constructor() {
    super();

    if (Store.Instance) {
      throw new Error("cannot instantiate root store multiple times");
    }

    Store.Instance = this;

    Store.PARSE_URL();
    this.init();
  }

  @action.bound async init() {
    try {
      await this.user.init();
      await this.cache.init();
      this.generalSearchStore = new GeneralSearchStore(this);

      this.setReady();

      registerDirectUrls(this.navigation);
      this.navigation.maybeTriggerDirectUrl();
    } catch (e) {
      console.error(e);
      this.setError(e);
      this.setReady();
    }
  }

  get shouldUpdate() {
    if (this.snoozeUpdateUntil > Date.now()) {
      return false;
    }

    return this.canUpdate;
  }

  @computed get canUpdate() {
    if (this.currentVersion === 0) {
      return false;
    }

    return this.currentVersion < this.minVersion;
  }

  setMinVersion(version: string) {
    if (!version) {
      return;
    }

    this.minVersion = semverToInt(version);
  }

  @action snoozeUpdate(minutes: number) {
    this.snoozeUpdateUntil = Date.now() + 60 * minutes * 1000;
  }
}

export function store() {
  return Store.Instance || window.store;
}

export function globalStore() {
  return store();
}
